import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PROVIDERS } from "../../../utils";

// Import icons
import fire from "../../../images/fire.svg";
import crash from "../../../images/crash.svg";
import virtuals from "../../../images/virtuals.svg";
import lite from "../../../images/lite.svg";
import drops from "../../../images/drops.svg";
import slots from "../../../images/slots.svg";
import table from "../../../images/table.svg";
import live from "../../../images/live.svg";
import top from "../../../images/top.svg";
import newIcon from "../../../images/new.svg";
import { useGetCategoriesQuery } from "../../../store/apis";

// Skeleton loaders for category and provider items
const CategorySkeleton = () => (
  <div className="rounded-md bg-gray-200 dark:bg-bluedarkmode p-[4px_6px] s15:p-[8px_10px] s18:p-[10px] flex justify-between items-center gap-3 animate-pulse">
    <div className="flex flex-row items-center gap-2 s15:gap-3">
      <div className="w-3 h-3 min-w-3 max-w-3 s15:w-4 s15:h-4 s15:min-w-4 s15:max-w-4 bg-gray-300 dark:bg-darktitleColor rounded-full"></div>
      <div className="h-3 w-16 bg-gray-300 dark:bg-darktitleColor rounded-sm"></div>
    </div>
    <div className="h-3 w-6 bg-gray-300 dark:bg-darktitleColor rounded-sm"></div>
  </div>
);

const ProviderSkeleton = () => (
  <div className="flex items-center gap-3 p-[10px] rounded-md w-full animate-pulse">
    <div className="w-4 h-4 bg-gray-300 dark:bg-darktitleColor rounded-full"></div>
    <div className="h-3 w-20 bg-gray-300 dark:bg-darktitleColor rounded-sm"></div>
  </div>
);

const CasinoSidebar = ({
  selectedProvider,
  setSelectedProvider,
  activeCategory,
  onCategoryChange,
}) => {
  // Track the active filter type (category or provider)
  const [activeFilterType, setActiveFilterType] = useState(
    activeCategory ? "category" : selectedProvider ? "provider" : null
  );

  const handleCategoryClick = (e, categoryName) => {
    e.preventDefault(); // Prevent default link behavior

    // If selecting a category, clear any selected provider
    if (selectedProvider) {
      setSelectedProvider(null);
    }

    // Set the active filter type to category
    setActiveFilterType("category");

    // Update the active category
    onCategoryChange(categoryName);
  };

  const handleProviderClick = (provider) => {
    // If selecting a provider, clear active category
    if (activeCategory) {
      onCategoryChange(null);
    }

    // Set the active filter type to provider
    setActiveFilterType("provider");

    // Update the selected provider
    setSelectedProvider(provider);
  };

  const { data: categoriesData, isLoading: isCategoriesLoading } =
    useGetCategoriesQuery();
  const [categories, setCategories] = useState([
    { id: 1, linktext: "/", name: "All", image: fire, count: null },
    { id: 2, linktext: "/", name: "Top", image: top, count: null },
    { id: 3, linktext: "/", name: "New", image: newIcon, count: null },
    { id: 4, linktext: "/", name: "PragmaticLive", image: live, count: 0 },
    { id: 5, linktext: "/", name: "Crash", image: crash, count: 0 },
    { id: 6, linktext: "/", name: "Virtuals", image: virtuals, count: 0 },
    { id: 7, linktext: "/", name: "Lite", image: lite, count: 0 },
    { id: 8, linktext: "/", name: "Drops and …", image: drops, count: 0 },
    { id: 9, linktext: "/", name: "Slots", image: slots, count: 0 },
    { id: 10, linktext: "/", name: "Table/Cards", image: table, count: 0 },
  ]);

  // Determine overall loading state - either from prop or query
  const isLoadingState = isCategoriesLoading;

  useEffect(() => {
    if (categoriesData && Array.isArray(categoriesData)) {
      // Update categories with counts from API
      setCategories((prevCategories) => {
        return prevCategories.map((category) => {
          // Try to find matching category in the API data (case insensitive)
          const matchingCategory = categoriesData.find(
            (apiCat) =>
              apiCat.category?.toLowerCase() === category.name.toLowerCase()
          );

          // If found, update the count, otherwise keep the existing count
          if (matchingCategory) {
            return {
              ...category,
              count: matchingCategory.game_count || 0,
            };
          }

          // Special case for "All" - sum all game counts
          if (category.name === "All") {
            const totalCount = categoriesData.reduce(
              (sum, apiCat) => sum + (apiCat.game_count || 0),
              0
            );
            return {
              ...category,
              count: totalCount,
            };
          }

          return category;
        });
      });
    }
  }, [categoriesData]);

  // Function to render category skeletons
  const renderCategorySkeletons = () => {
    return Array(8)
      .fill(0)
      .map((_, index) => <CategorySkeleton key={`cat-skeleton-${index}`} />);
  };

  // Function to render provider skeletons
  const renderProviderSkeletons = () => {
    return Array(5)
      .fill(0)
      .map((_, index) => <ProviderSkeleton key={`prov-skeleton-${index}`} />);
  };

  return (
    <div className="w-full flex flex-col gap-[10px]">
      {/* New Games Section */}
      <div className="rounded-md bg-white dark:bg-bluedarkmode p-[10px_12px] s15:p-[10px_12px] s18:py-[17px] s18:px-5 flex gap-2 s15:gap-3">
        <img
          src={fire}
          alt="fire"
          className="object-contain dark:invert dark:hover:invert-0"
        />
        <p className="text-xs s14:text-sm font-normal text-[#26262699] dark:text-white/60">
          New
        </p>
      </div>

      {/* Categories Section */}
      <div className="rounded-md bg-white dark:bg-bluedarkmode p-[10px_6px] s15:p-[10px] flex flex-col gap-1 s14:gap-2 s15:gap-[10px]">
        <p className="text-[#A3A3A3] dark:text-white text-xs font-normal">
          Categories
        </p>
        {isLoadingState
          ? renderCategorySkeletons()
          : categories.map((category) => (
              <CategoryItem
                key={category.id}
                category={category}
                isActive={
                  activeFilterType === "category" &&
                  activeCategory === category.name
                }
                onClick={(e) => handleCategoryClick(e, category.name)}
              />
            ))}
      </div>

      {/* Providers Section */}
      <div className="rounded-md bg-white dark:bg-bluedarkmode p-[10px_6px] s15:p-[10px] flex flex-col gap-1 s14:gap-2 s18:gap-[10px]">
        <p className="text-[#A3A3A3] dark:text-white text-xs font-normal">
          Providers
        </p>
        {isLoadingState
          ? renderProviderSkeletons()
          : Object.values(PROVIDERS).map((provider) => (
              <ProviderItem
                key={provider.id}
                provider={provider}
                isSelected={
                  activeFilterType === "provider" &&
                  selectedProvider?.id === provider.id
                }
                onSelect={() => handleProviderClick(provider)}
              />
            ))}
      </div>
    </div>
  );
};

// Category Item Component
const CategoryItem = ({ category, isActive, onClick }) => (
  <Link
    to={category.linktext}
    onClick={onClick}
    className={`rounded-md ${
      isActive
        ? "bg-yellow"
        : "bg-white dark:bg-transparent dark:hover:bg-yellow hover:bg-yellow"
    } 
    p-[4px_6px] s15:p-[8px_10px] s18:p-[10px] flex justify-between items-center gap-3 transition-all duration-300 group`}
  >
    <div className="flex flex-row items-center gap-2 s15:gap-3">
      <span className="w-3 min-w-3 max-w-3 s15:w-4 s15:min-w-4 s15:max-w-4">
        {category.image && (
          <img
            src={category.image}
            alt={category.name}
            className={`object-contain w-4 h-4 ${
              isActive ? "" : "dark:invert dark:group-hover:invert-0"
            }`}
          />
        )}
      </span>
      <p
        className={`text-xs s15:text-sm font-normal !leading-none ${
          isActive
            ? "text-[#26262699]"
            : "text-[#26262699] dark:text-white/60 dark:group-hover:text-[#26262699]"
        } capitalize`}
      >
        {category.name}
      </p>
    </div>
    {category.count !== null && (
      <p
        className={`text-xs s15:text-sm font-normal ${
          isActive
            ? "text-[#26262699]"
            : "text-[#26262699] dark:text-white/60 dark:group-hover:text-[#26262699]"
        }`}
      >
        {category.count}
      </p>
    )}
  </Link>
);

// Provider Item Component
const ProviderItem = ({ provider, isSelected, onSelect }) => (
  <button
    onClick={onSelect}
    className={`flex items-center gap-3 p-[10px] rounded-md w-full text-left
     hover:bg-yellow dark:hover:bg-yellow transition-all duration-300 group
     ${isSelected ? "bg-yellow" : ""}`}
  >
    <img
      src={provider.icon}
      alt={provider.name}
      className="w-4 h-4 dark:invert dark:group-hover:invert-0"
    />
    <span
      className="text-sm font-normal text-[#26262699] dark:text-white/60 
     dark:group-hover:text-[#26262699]"
    >
      {provider.name}
    </span>
  </button>
);

export default CasinoSidebar;
